"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Concept",
  desc: "Initial consultations to understand your vision, aesthetic preferences, and event goals. We establish the foundation for a truly bespoke experience."
}, {
  title: "Curation",
  desc: "Sourcing the perfect venue, assembling a bespoke vendor team, and developing a cohesive design narrative tailored to your unique style."
}, {
  title: "Coordination",
  desc: "Meticulous logistical planning, timeline creation, and seamless communication with all parties to ensure flawless execution."
}, {
  title: "Execution",
  desc: "Flawless on-site management, ensuring every detail is perfect so you can immerse yourself fully in the joy of your event."
}];
function TimelineStep({
  step,
  index,
  isLast
}: {
  step: typeof steps[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.4
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className="relative flex flex-col md:flex-row items-start w-full mb-24 last:mb-0 group">
      {}
      <div className={cn("absolute left-[1.4rem] md:left-1/2 w-3 h-3 rounded-full border-2 border-chart-1 md:-translate-x-1/2 z-10 mt-2.5 transition-colors duration-700 ease-in-out", isVisible ? "bg-chart-1" : "bg-background")} />

      {}
      {!isLast && <div className={cn("absolute left-[1.4rem] md:left-1/2 top-2.5 w-px h-[calc(100%+6rem)] bg-chart-1 md:-translate-x-1/2 z-0 origin-top transition-transform duration-1000 ease-out", isVisible ? "scale-y-100" : "scale-y-0")} />}

      {}
      <div className={cn("w-full md:w-1/2 pl-16 md:pl-0 transition-all duration-700 delay-200", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8", isEven ? "md:pr-20 md:text-right" : "md:pl-20 md:ml-auto")}>
        <span className="text-chart-1 font-mono text-xs tracking-widest uppercase mb-3 block">Phase 0{index + 1}</span>
        <h3 className="text-2xl md:text-3xl font-serif font-medium text-foreground mb-4">{step.title}</h3>
        <p className="text-muted-foreground leading-relaxed text-lg">{step.desc}</p>
      </div>
    </div>;
}
export default function TheBlueprint() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="the-blueprint" className="py-24 md:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div ref={headerRef} className={cn("text-center max-w-2xl mx-auto mb-24 transition-all duration-700", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-foreground mb-6">The Blueprint</h2>
          <p className="text-lg text-muted-foreground">
            Our meticulous four-step process for curating unforgettable, upscale experiences for echelon soiree clients.
          </p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {}
          <div className="absolute left-[1.4rem] md:left-1/2 top-2.5 bottom-2.5 w-px bg-border md:-translate-x-1/2 z-0" />

          {steps.map((step, i) => <TimelineStep key={i} step={step} index={i} isLast={i === steps.length - 1} />)}
        </div>
      </div>
    </section>;
}